import type { CurrentAuthorization } from '@/api/core/authorization';
import type {
  CatalogRoom,
  CatalogSlot,
  PropertyCatalog,
  PropertyCatalogCursors,
} from '@/api/core/properties';
import type { MediaTarget } from '@/api/core/property-media';
import { ZoomableImg } from '@/components/zoomable-img';
import { safeImageUri } from '@/lib/safe-image-uri';
import {
  Button,
  DataTable,
  DetailPage,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useInsertionEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { photoCaption } from '../media/media-presentation';
import { PHOTO_PREVIEW_LIMIT, PhotoToggle } from '../photo-toggle';
import type { PropertyRuntime } from '../property-queries';
import { areaLabel, businessFact, featureLabel } from './room-facts';
import { SlotOccupancySection } from './slot-occupancy-section';
import { UnitSaleClassificationSection } from './unit-sale-classification-section';

const roomColumns = (
  slotCountByRoom: Map<string, number>,
  sortable: boolean,
): DataTableColumn<CatalogRoom>[] => [
  {
    key: 'roomNo',
    sortKey: 'room_no',
    sortable,
    header: '호실',
    cell: (room) => `${String(room.roomNo)}호`,
  },
  {
    key: 'typeName',
    sortable: false,
    header: '객실 유형',
    cell: (room) => room.typeName ?? '정보 없음',
  },
  {
    key: 'floor',
    sortKey: 'floor',
    sortable,
    header: '층',
    cell: (room) => (room.floor === null ? '정보 없음' : `${String(room.floor)}층`),
  },
  {
    key: 'slotCapacity',
    sortable: false,
    header: '수용 기준',
    cell: (room) => (room.slotCapacity === null ? '정보 없음' : String(room.slotCapacity)),
  },
  {
    key: 'relatedSlots',
    sortable: false,
    header: '연결 슬롯',
    cell: (room) => `${String(slotCountByRoom.get(room.id) ?? 0)}개`,
  },
];
const selectedRoomByView = new WeakMap<object, string | null>();
const selectedSlotByView = new WeakMap<object, string | null>();

function canonicalPresentation(page: PropertyCatalog, targetKind: string, targetId: string) {
  return (
    page.presentations.find(
      (presentation) =>
        presentation.targetKind === targetKind &&
        (targetKind === 'ROOM' ? presentation.roomId : presentation.roomTypeId) === targetId,
    ) ?? null
  );
}

function canonicalRoomTypePhotos(page: PropertyCatalog, roomTypeId: string) {
  const groups = page.mediaGroups
    .filter(
      (group) =>
        group.ownerScopeKind === 'ROOM_TYPE' &&
        group.ownerRoomTypeId?.toLowerCase() === roomTypeId.toLowerCase(),
    )
    .sort(
      (a, b) =>
        (a.sourceOrder ?? Number.MAX_SAFE_INTEGER) - (b.sourceOrder ?? Number.MAX_SAFE_INTEGER),
    );
  const result = {
    photos: [] as { uri: string; label: string; groupId: string; itemId: string }[],
    unavailableCount: 0,
  };
  for (const group of groups) {
    const items = page.mediaItems
      .filter((item) => item.groupId === group.id)
      .sort(
        (a, b) =>
          (a.sourceOrder ?? Number.MAX_SAFE_INTEGER) - (b.sourceOrder ?? Number.MAX_SAFE_INTEGER),
      );
    for (const item of items) {
      if (item.kind !== 'IMAGE') continue;
      const value = item.primaryUri ?? item.thumbnailUri;
      const uri = safeImageUri(value);
      if (!uri) {
        result.unavailableCount += 1;
        continue;
      }
      result.photos.push({
        uri,
        label: photoCaption(group, item.contentSlot),
        groupId: group.id,
        itemId: item.id,
      });
    }
  }
  return result;
}

/** 원본이 슬롯 번호를 비워 두면 "슬롯 번호 "만 덩그러니 남는다 — 없다고 적는다. */
const slotNumber = (slot: CatalogSlot) => slot.slotNo.trim() || '없음';

export function canonicalSlotName(page: PropertyCatalog, slotId: string): string | null {
  const names = page.presentations
    .filter((presentation) => presentation.targetKind === 'SLOT' && presentation.slotId === slotId)
    .flatMap((presentation) =>
      presentation.texts
        .filter((item) => item.purpose === 'DISPLAY_NAME' && item.language === 'und')
        .map((item) => item.value?.trim() ?? '')
        .filter(Boolean),
    );
  return names.length === 1 ? (names[0] ?? null) : null;
}

interface RoomCatalogSectionProps {
  page: PropertyCatalog;
  cursors: PropertyCatalogCursors;
  onCursor: (key: 'room' | 'roomSort', value: string | null) => void;
  enableServerSorting: boolean;
  sort: string | null;
  onSortChange: (sort: string | null, field: 'room_no' | 'floor') => void;
  fetching: boolean;
  /** 다음 쪽을 받는 동안 이전 쪽을 그대로 보이고 있는 중이다 — 표를 흐리게 하고 조작을 막는다. */
  pending: boolean;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  isPropertyCurrent: () => boolean;
  authorization?: CurrentAuthorization | null;
  ownerBoundary?: object;
  /** 객실·슬롯의 저장된 미디어를 "사진·영상" 탭에서 여는 주소 — 대상을 검색할 수 없어 여기서 들어간다. */
  mediaHref?: (target: MediaTarget) => string;
  /** 객실 탭이 보이는 중이다 — 숨겨지면 선택한 객실을 비워, 돌아왔을 때 상세 다이얼로그가 다시 뜨지 않게 한다. */
  visible?: boolean;
}

export function RoomCatalogSection(props: RoomCatalogSectionProps) {
  const scope = `${props.propertyId}:${String(props.page.property.branch_id)}:${props.cursors.roomSort ?? props.cursors.room ?? ''}`;
  const [localUnitCommandBoundary] = useState<object>(() => ({}));
  const unitCommandBoundary = props.ownerBoundary ?? localUnitCommandBoundary;
  return (
    <RoomCatalogSectionView key={scope} {...props} unitCommandBoundary={unitCommandBoundary} />
  );
}

function RoomCatalogSectionView({
  page,
  cursors,
  onCursor,
  enableServerSorting,
  sort,
  onSortChange,
  fetching,
  pending,
  runtime,
  generation,
  propertyId,
  isPropertyCurrent,
  authorization = null,
  mediaHref,
  visible = true,
  unitCommandBoundary,
}: RoomCatalogSectionProps & { unitCommandBoundary: object }) {
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [roomSearch, setRoomSearch] = useState('');
  const [floorFilter, setFloorFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [failedPhotoKeys, setFailedPhotoKeys] = useState<Set<string>>(() => new Set());
  const [expandedTypeIds, setExpandedTypeIds] = useState<ReadonlySet<string>>(() => new Set());
  const selectionView = useMemo(() => ({}), []);
  const roomById = new Map(page.rooms.map((room) => [room.id, room]));
  const floorOptions = [...new Set(page.rooms.map((room) => room.floor))].sort((a, b) => {
    if (a === null) return b === null ? 0 : 1;
    if (b === null) return -1;
    return a - b;
  });
  const typeNameById = new Map(page.roomTypes.map((type) => [type.sourceRoomTypeId, type.name]));
  const roomTypeIds = [...new Set(page.rooms.map((room) => room.typeId))].sort((a, b) => a - b);
  const floorSelectItems = [
    { value: 'all', label: '모든 층' },
    ...floorOptions.map((floor) => ({
      value: floor === null ? 'null' : String(floor),
      label: floor === null ? '층 정보 없음' : `${String(floor)}층`,
    })),
  ];
  const roomTypeSelectItems = [
    { value: 'all', label: '모든 유형' },
    ...roomTypeIds.map((typeId) => {
      const name =
        typeNameById.get(typeId) ??
        page.rooms.find((room) => room.typeId === typeId)?.typeName ??
        null;
      return {
        value: String(typeId),
        label: name ?? '이름 없는 객실 유형',
      };
    }),
  ];
  const normalizedSearch = roomSearch.trim().toLocaleLowerCase('ko-KR');
  const matchesRoomFilters = (room: CatalogRoom, search: string, floor: string, type: string) =>
    String(room.roomNo)
      .toLocaleLowerCase('ko-KR')
      .includes(search.trim().toLocaleLowerCase('ko-KR')) &&
    (floor === 'all' || (floor === 'null' ? room.floor === null : String(room.floor) === floor)) &&
    (type === 'all' || String(room.typeId) === type);
  const visibleRooms = page.rooms.filter((room) =>
    matchesRoomFilters(room, roomSearch, floorFilter, typeFilter),
  );
  const selectedRoom = visibleRooms.find((room) => room.id === selectedRoomId) ?? null;
  const selectedSlots = selectedRoom
    ? page.slots.filter((slot) => slot.roomId === selectedRoom.id)
    : [];
  const selectedSlot = selectedSlots.find((slot) => slot.id === selectedSlotId) ?? null;
  const selectedRoomPresentation = selectedRoom
    ? canonicalPresentation(page, 'ROOM', selectedRoom.id)
    : null;
  const selectedRoomType = selectedRoom
    ? page.roomTypes.find((type) => type.sourceRoomTypeId === selectedRoom.typeId)
    : null;
  const selectedRoomTypePresentation = selectedRoomType
    ? canonicalPresentation(page, 'ROOM_TYPE', selectedRoomType.id)
    : null;
  const areaValues = selectedRoomPresentation?.features.area_values;
  const areaFeatures =
    areaValues && typeof areaValues === 'object' && !Array.isArray(areaValues)
      ? (areaValues as Record<string, unknown>)
      : {};
  const sourceRoomFacts = [
    ['전용 면적', areaLabel(areaFeatures.exclusive)],
    ['공용 면적', areaLabel(areaFeatures.common)],
    ['시설', businessFact(selectedRoomTypePresentation?.features.facilities, featureLabel)],
    ['가구', businessFact(selectedRoomTypePresentation?.features.furniture, featureLabel)],
    ['특징', businessFact(selectedRoomTypePresentation?.features.tags, featureLabel)],
  ].filter((fact): fact is [string, string] => fact[1] !== null);
  const galleryByType = new Map(
    page.roomTypes.map((type) => [type.id, canonicalRoomTypePhotos(page, type.id)]),
  );
  const slotName = (slot: CatalogSlot) => {
    return canonicalSlotName(page, slot.id) ?? '미등록';
  };
  useInsertionEffect(() => {
    selectedRoomByView.set(selectionView, selectedRoom?.id ?? null);
    selectedSlotByView.set(selectionView, selectedSlot?.id ?? null);
    return () => {
      selectedRoomByView.set(selectionView, null);
      selectedSlotByView.set(selectionView, null);
    };
  }, [selectedRoom?.id, selectedSlot?.id, selectionView]);
  const clearRoomSelection = () => {
    selectedRoomByView.set(selectionView, null);
    selectedSlotByView.set(selectionView, null);
    setSelectedSlotId(null);
    setSelectedRoomId(null);
  };
  // 다른 탭(사진·영상 등)으로 나갔다 돌아오면 상세 다이얼로그를 다시 띄우지 않는다.
  // (선택을 비우면 insertion effect가 조회 선택 기록도 함께 비운다.)
  const [wasVisible, setWasVisible] = useState(visible);
  if (wasVisible !== visible) {
    setWasVisible(visible);
    if (!visible) {
      setSelectedSlotId(null);
      setSelectedRoomId(null);
    }
  }
  const updateRoomFilters = (search: string, floor: string, type: string) => {
    const selected = selectedRoomId === null ? null : (roomById.get(selectedRoomId) ?? null);
    if (selected && !matchesRoomFilters(selected, search, floor, type)) clearRoomSelection();
    setRoomSearch(search);
    setFloorFilter(floor);
    setTypeFilter(type);
  };
  const resetRoomFilters = () => {
    updateRoomFilters('', 'all', 'all');
  };
  const slotCountByRoom = new Map<string, number>();
  for (const slot of page.slots)
    slotCountByRoom.set(slot.roomId, (slotCountByRoom.get(slot.roomId) ?? 0) + 1);
  const selectRoom = (room: CatalogRoom) => {
    selectedRoomByView.set(selectionView, room.id);
    selectedSlotByView.set(selectionView, null);
    setSelectedSlotId(null);
    setSelectedRoomId(room.id);
  };
  const closeRoomDialog = () => {
    selectedRoomByView.set(selectionView, null);
    selectedSlotByView.set(selectionView, null);
    setSelectedSlotId(null);
    setSelectedRoomId(null);
  };
  const columns = roomColumns(slotCountByRoom, enableServerSorting);
  const roomNextCursor = cursors.sortBy ? page.roomSortNextCursor : page.roomNextCursor;
  const roomCursor = cursors.sortBy ? cursors.roomSort : cursors.room;
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <DetailPage.Section
        aria-label="객실 운영 정보"
        title="객실"
        actions={
          page.roomTypes.length > 0 ? (
            <a
              className="body text-primary underline-offset-4 hover:underline"
              href="#room-type-gallery-title"
            >
              객실 유형 사진 보기
            </a>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-3">
          {/* 같은 자리의 상태 줄이 개수 ↔ 로딩 안내로 바뀐다 — 줄을 새로 끼우면 아래가 밀린다. */}
          <p
            className={cn('body', pending && 'flex items-center gap-2')}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {pending ? (
              <>
                <Spinner aria-hidden className="shrink-0" />
                객실 목록을 불러오고 있습니다.
              </>
            ) : (
              <>
                전체 {page.totalRoomCount}개 · 현재 페이지 {page.rooms.length}개 · 표시{' '}
                {visibleRooms.length}개{visibleRooms.length === 0 ? ' · 표시 결과 없음' : ''}
              </>
            )}
          </p>
          <div
            inert={pending}
            className={cn(
              'grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto]',
              pending && 'opacity-60',
            )}
          >
            <div className="flex min-w-0 flex-col gap-1.5">
              <label className="label" htmlFor="room-catalog-search">
                현재 페이지에서 호실 찾기
              </label>
              <Input
                id="room-catalog-search"
                value={roomSearch}
                placeholder="호실 번호 입력"
                onChange={(event) => {
                  updateRoomFilters(event.target.value, floorFilter, typeFilter);
                }}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="label">층</span>
              <div className="flex min-w-0">
                <Select
                  value={floorFilter}
                  items={floorSelectItems}
                  onValueChange={(value) => {
                    if (value !== null) updateRoomFilters(roomSearch, value, typeFilter);
                  }}
                >
                  <SelectTrigger aria-label="층" className="min-w-0 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">모든 층</SelectItem>
                    {floorOptions.map((floor) => {
                      const value = floor === null ? 'null' : String(floor);
                      return (
                        <SelectItem key={value} value={value}>
                          {floor === null ? '층 정보 없음' : `${String(floor)}층`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="label">객실 유형</span>
              <div className="flex min-w-0">
                <Select
                  value={typeFilter}
                  items={roomTypeSelectItems}
                  onValueChange={(value) => {
                    if (value !== null) updateRoomFilters(roomSearch, floorFilter, value);
                  }}
                >
                  <SelectTrigger aria-label="객실 유형" className="min-w-0 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">모든 유형</SelectItem>
                    {roomTypeIds.map((typeId) => {
                      const name =
                        typeNameById.get(typeId) ??
                        page.rooms.find((room) => room.typeId === typeId)?.typeName ??
                        null;
                      return (
                        <SelectItem key={typeId} value={String(typeId)}>
                          {name ?? '이름 없는 객실 유형'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              aria-label="검색·필터 초기화"
              onClick={() => {
                resetRoomFilters();
                document.getElementById('room-catalog-search')?.focus();
              }}
            >
              초기화
            </Button>
          </div>
          {(roomCursor !== null || roomNextCursor !== null) && (
            <nav aria-label="객실 페이지 이동" className="flex flex-wrap gap-2">
              {roomCursor && (
                <Button
                  variant="outline"
                  onClick={() => {
                    resetRoomFilters();
                    onCursor(cursors.sortBy ? 'roomSort' : 'room', null);
                  }}
                >
                  객실 처음으로
                </Button>
              )}
              {roomNextCursor && (
                <Button
                  variant="outline"
                  disabled={fetching}
                  onClick={() => {
                    resetRoomFilters();
                    onCursor(cursors.sortBy ? 'roomSort' : 'room', roomNextCursor);
                  }}
                >
                  다음 객실 페이지
                </Button>
              )}
            </nav>
          )}
          <div
            role="region"
            aria-label="객실 표"
            aria-busy={pending || undefined}
            aria-describedby={visibleRooms.length > 0 ? 'room-catalog-scroll-hint' : undefined}
            inert={pending}
            className={cn(
              'min-w-0 [&_[data-slot=empty-description]]:break-keep [&_[data-slot=empty-title]]:break-keep',
              pending && 'opacity-60',
            )}
          >
            {visibleRooms.length > 0 && (
              <p id="room-catalog-scroll-hint" className="body text-muted-foreground sm:hidden">
                좁은 화면에서는 표를 좌우로 밀어 추가 열을 확인할 수 있습니다.
              </p>
            )}
            <DataTable
              columns={columns}
              data={visibleRooms}
              sort={enableServerSorting ? sort : null}
              onSortChange={
                enableServerSorting
                  ? (nextSort) => {
                      const field = nextSort?.split(',')[0] ?? sort?.split(',')[0];
                      if (field === 'room_no' || field === 'floor') onSortChange(nextSort, field);
                    }
                  : undefined
              }
              rowKey={(room) => room.id}
              onRowClick={selectRoom}
              empty={{
                title:
                  page.rooms.length > 0 &&
                  (normalizedSearch || floorFilter !== 'all' || typeFilter !== 'all')
                    ? '조건에 맞는 객실이 없습니다.'
                    : roomCursor
                      ? '이 페이지에 객실이 없습니다'
                      : page.totalRoomCount === 0
                        ? '등록된 객실이 없습니다'
                        : '이번 조회에 객실 정보가 없습니다',
                description:
                  page.rooms.length > 0 &&
                  (normalizedSearch || floorFilter !== 'all' || typeFilter !== 'all')
                    ? '검색어 또는 필터를 바꾸거나 초기화해 주세요.'
                    : roomCursor
                      ? '다른 객실 페이지의 자료는 여기에서 조회하지 않았습니다.'
                      : '객실 등록 여부와 검색 조건을 확인해 주세요.',
              }}
            />
          </div>
          <nav aria-label="하단 객실 페이지 이동" className="flex gap-2">
            {roomCursor && (
              <Button
                variant="outline"
                onClick={() => {
                  resetRoomFilters();
                  onCursor(cursors.sortBy ? 'roomSort' : 'room', null);
                }}
              >
                객실 처음으로
              </Button>
            )}
            {roomNextCursor && (
              <Button
                variant="outline"
                disabled={fetching}
                onClick={() => {
                  resetRoomFilters();
                  onCursor(cursors.sortBy ? 'roomSort' : 'room', roomNextCursor);
                }}
              >
                다음 객실 페이지
              </Button>
            )}
          </nav>
        </div>

        {selectedRoom && (
          <Dialog
            open={visible}
            onOpenChange={(open) => {
              if (!open) closeRoomDialog();
            }}
          >
            <DialogContent className="sm:max-w-5xl">
              <DialogHeader>
                <DialogTitle>{String(selectedRoom.roomNo)}호 객실 상세</DialogTitle>
              </DialogHeader>
              <DialogBody className="flex max-h-[70vh] min-w-0 flex-col gap-4 overflow-y-auto">
                <section role="region" aria-label={`${String(selectedRoom.roomNo)}호 객실 상세`}>
                  <div className="mb-3 flex">
                    {mediaHref ? (
                      <Button
                        nativeButton={false}
                        role="link"
                        variant="outline"
                        className="w-full sm:w-auto"
                        render={
                          <Link
                            to={mediaHref({ kind: 'ROOM', id: selectedRoom.id, floor: null })}
                          />
                        }
                      >
                        이 객실 사진·영상 보기
                      </Button>
                    ) : null}
                  </div>
                  <dl className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="min-w-0">
                      <dt className="caption text-muted-foreground">객실 유형</dt>
                      <dd className="body-strong mt-1 break-words">
                        {selectedRoom.typeName ?? '정보 없음'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="caption text-muted-foreground">층</dt>
                      <dd className="body-strong mt-1">
                        {selectedRoom.floor === null
                          ? '정보 없음'
                          : `${String(selectedRoom.floor)}층`}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="caption text-muted-foreground">수용 기준</dt>
                      <dd className="body-strong mt-1">
                        {selectedRoom.slotCapacity === null
                          ? '정보 없음'
                          : String(selectedRoom.slotCapacity)}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="caption text-muted-foreground">관리 대상</dt>
                      <dd className="body-strong mt-1">
                        {selectedRoom.selectedForManagement === null
                          ? '정보 없음'
                          : selectedRoom.selectedForManagement
                            ? '선택됨'
                            : '선택 안 됨'}
                      </dd>
                    </div>
                  </dl>
                  {sourceRoomFacts.length > 0 && (
                    <dl
                      aria-label="객실 정보"
                      className="border-border mt-4 grid min-w-0 gap-3 border-t pt-4 sm:grid-cols-2 xl:grid-cols-3"
                    >
                      {sourceRoomFacts.map(([label, value]) => (
                        <div key={label} className="min-w-0 break-words">
                          <dt className="caption text-muted-foreground">{label}</dt>
                          <dd className="body mt-1 break-keep">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <h4 className="title-sm mt-4">연결 슬롯 ({String(selectedSlots.length)}개)</h4>
                  {selectedSlots.length ? (
                    <ul
                      aria-label={`${String(selectedRoom.roomNo)}호 연결 슬롯`}
                      className="mt-2 flex flex-col gap-2"
                    >
                      {selectedSlots.map((slot) => (
                        <li
                          key={slot.id}
                          className="border-border flex min-w-0 flex-col gap-2 rounded border p-3"
                        >
                          <div className="flex flex-wrap gap-x-3">
                            <span className="body-strong">{slotName(slot)}</span>
                            <span className="body text-muted-foreground">
                              슬롯 번호 {slotNumber(slot)}
                            </span>
                          </div>
                          <div className="body text-muted-foreground flex min-w-0 flex-wrap gap-x-3 gap-y-1">
                            <span>
                              수용 기준{' '}
                              {slot.physicalCapacity === null
                                ? '정보 없음'
                                : String(slot.physicalCapacity)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                            <Button
                              type="button"
                              variant="outline"
                              aria-pressed={selectedSlotId === slot.id}
                              aria-label={`${String(selectedRoom.roomNo)}호 ${slotName(slot)} 슬롯 번호 ${slotNumber(slot)} 기간별 점유 조회`}
                              className="w-full sm:w-auto"
                              onClick={() => {
                                selectedSlotByView.set(selectionView, slot.id);
                                setSelectedSlotId(slot.id);
                              }}
                            >
                              기간별 점유 조회
                            </Button>
                            {mediaHref ? (
                              <Button
                                nativeButton={false}
                                role="link"
                                variant="outline"
                                aria-label={`${String(selectedRoom.roomNo)}호 ${slotName(slot)} 슬롯 번호 ${slotNumber(slot)} 사진·영상 보기`}
                                className="w-full sm:w-auto"
                                render={
                                  <Link
                                    to={mediaHref({ kind: 'SLOT', id: slot.id, floor: null })}
                                  />
                                }
                              >
                                사진·영상 보기
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="body text-muted-foreground">연결된 슬롯이 없습니다.</p>
                  )}
                  {selectedSlot && (
                    <>
                      <SlotOccupancySection
                        key={selectedSlot.id}
                        propertyId={propertyId}
                        roomId={selectedRoom.id}
                        roomNo={selectedRoom.roomNo}
                        slotId={selectedSlot.id}
                        slotNo={selectedSlot.slotNo}
                        generation={generation}
                        runtime={runtime}
                        isCurrent={() =>
                          isPropertyCurrent() &&
                          selectedRoomByView.get(selectionView) === selectedRoom.id &&
                          selectedSlotByView.get(selectionView) === selectedSlot.id
                        }
                      />
                      <UnitSaleClassificationSection
                        key={`${selectedSlot.id}:sale-classification`}
                        runtime={runtime}
                        ownerBoundary={unitCommandBoundary}
                        generation={generation}
                        propertyId={propertyId}
                        room={selectedRoom}
                        slot={selectedSlot}
                        page={page}
                        authorization={authorization}
                        isCurrent={() =>
                          isPropertyCurrent() &&
                          selectedRoomByView.get(selectionView) === selectedRoom.id &&
                          selectedSlotByView.get(selectionView) === selectedSlot.id
                        }
                      />
                    </>
                  )}
                </section>
              </DialogBody>
            </DialogContent>
          </Dialog>
        )}
      </DetailPage.Section>

      <DetailPage.Section
        aria-labelledby="room-type-gallery-title"
        title={
          <span id="room-type-gallery-title" className="block scroll-mt-6">
            객실 유형 사진
          </span>
        }
      >
        {page.roomTypes.length === 0 ? (
          <p className="body text-muted-foreground">등록된 객실 유형이 없습니다.</p>
        ) : (
          <div className="flex flex-col gap-6">
            {page.roomTypes.map((type) => {
              const gallery = galleryByType.get(type.id) ?? { photos: [], unavailableCount: 0 };
              const photos = gallery.photos;
              const expanded = expandedTypeIds.has(type.id);
              const shownPhotos = expanded ? photos : photos.slice(0, PHOTO_PREVIEW_LIMIT);
              return (
                <section
                  key={type.id}
                  aria-label={`${type.name} 객실 유형 사진`}
                  className="flex flex-col gap-2"
                >
                  <h3 className="body-strong">{type.name}</h3>
                  {photos.length > 0 ? (
                    <ul className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {shownPhotos.map((photo, index) => (
                        <li key={`${photo.itemId}-${String(index)}`}>
                          <figure>
                            {failedPhotoKeys.has(`${photo.itemId}\u0000${photo.uri}`) ? (
                              <p role="status" className="body text-warning-foreground">
                                사진을 표시할 수 없습니다.
                              </p>
                            ) : (
                              <ZoomableImg
                                buttonClassName="w-full"
                                src={photo.uri}
                                alt={`${type.name} ${photo.label}`}
                                loading="lazy"
                                decoding="async"
                                className="aspect-[4/3] max-h-64 w-full rounded-md object-cover"
                                onError={() => {
                                  setFailedPhotoKeys((current) =>
                                    new Set(current).add(`${photo.itemId}\u0000${photo.uri}`),
                                  );
                                }}
                              />
                            )}
                            <figcaption className="caption text-muted-foreground mt-1">
                              {photo.label}
                            </figcaption>
                          </figure>
                        </li>
                      ))}
                    </ul>
                  ) : gallery.unavailableCount > 0 ? (
                    <p className="body text-warning-foreground">사진을 표시할 수 없습니다.</p>
                  ) : (
                    <p className="body text-muted-foreground">등록된 사진이 없습니다.</p>
                  )}
                  <PhotoToggle
                    subject={`${type.name} 사진`}
                    total={photos.length}
                    expanded={expanded}
                    onExpand={() => {
                      setExpandedTypeIds((current) => new Set(current).add(type.id));
                    }}
                  />
                  {photos.length > 0 && gallery.unavailableCount > 0 && (
                    <p role="status" className="body text-warning-foreground">
                      일부 사진을 표시할 수 없습니다.
                    </p>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </DetailPage.Section>
    </div>
  );
}
